import React from 'react';
import { createPortal } from 'react-dom';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { ReferenceOption } from './types';

type Props = {
  fieldKey: string;
  value: string;
  options: ReferenceOption[];
  onChange: (value: string) => void;
};

export const InterpolatedStringEditor: React.FC<Props> = ({
  fieldKey,
  value,
  options,
  onChange,
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [position, setPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  }>();
  const [insertedRefs, setInsertedRefs] = React.useState<Set<string>>(
    () => new Set()
  );

  const getTemplateRangeAt = React.useCallback<
    (pos: number) => {
      start: number;
      end: number;
      template: string;
    } | null
  >(
    (pos: number) => {
      let match: { start: number; end: number; template: string } | null = null;
      insertedRefs.forEach((tpl) => {
        let idx = value.indexOf(tpl);
        while (idx !== -1) {
          const start = idx;
          const end = idx + tpl.length;
          if (pos >= start && pos <= end) {
            match = { start, end, template: tpl };
            break;
          }
          idx = value.indexOf(tpl, idx + tpl.length);
        }
      });
      return match;
    },
    [insertedRefs, value]
  );

  const removeTemplateAtCursor = React.useCallback(
    (direction: 'backspace' | 'delete') => {
      const el = textareaRef.current;
      if (!el) return false;
      const startSel = el.selectionStart ?? 0;
      const endSel = el.selectionEnd ?? startSel;

      // If a selection exists, let the default behavior handle deletion
      if (startSel !== endSel) return false;

      const checkPos =
        direction === 'backspace' ? Math.max(0, startSel - 1) : startSel;
      const range = getTemplateRangeAt(checkPos);
      if (!range) return false;

      const nextValue = `${value.slice(0, range.start)}${value.slice(range.end)}`;
      onChange(nextValue);
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(range.start, range.start);
      });
      return true;
    },
    [getTemplateRangeAt, onChange, value]
  );

  const updateDropdownPosition = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        if (dropdownRef.current && dropdownRef.current.contains(e.target)) {
          return;
        }
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    const onResize = () => updateDropdownPosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);

    return () => {
      document.removeEventListener('mousedown', onDocClick);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updateDropdownPosition]);

  React.useEffect(() => {
    if (!insertedRefs.size) return;
    setInsertedRefs((prev) => {
      const next = new Set<string>();
      prev.forEach((tpl) => {
        if (value.includes(tpl)) next.add(tpl);
      });
      return next;
    });
  }, [insertedRefs.size, value]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => opt.label.toLowerCase().includes(q));
  }, [options, query]);

  const insertTemplate = React.useCallback(
    (template: string) => {
      const el = textareaRef.current;
      if (!el) {
        onChange(`${value}${template}`);
        setInsertedRefs((prev) => {
          const next = new Set(prev);
          next.add(template);
          return next;
        });
        return;
      }
      const start = el.selectionStart ?? value.length;
      const end = el.selectionEnd ?? value.length;
      const next = `${value.slice(0, start)}${template}${value.slice(end)}`;
      onChange(next);
      setInsertedRefs((prev) => {
        const updated = new Set(prev);
        updated.add(template);
        return updated;
      });
      setOpen(false);
      setQuery('');
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + template.length;
        el.setSelectionRange(pos, pos);
      });
    },
    [onChange, value]
  );

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-start gap-2">
        <div className="relative flex-1">
          <div
            ref={overlayRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 whitespace-pre-wrap break-words px-2 py-1.5 text-sm text-slate-800 overflow-hidden"
          >
            {value.split(/(\{\{[^}]+\}\})/g).map((segment, idx) => {
              const isRef = segment.startsWith('{{') && segment.endsWith('}}');
              const shouldBadge = isRef && insertedRefs.has(segment);
              return shouldBadge ? (
                <span
                  key={`${fieldKey}-seg-${idx}`}
                  className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold border border-slate-200 align-middle"
                >
                  {segment.replace(/^\{\{|\}\}$/g, '')}
                </span>
              ) : (
                <span key={`${fieldKey}-seg-${idx}`}>{segment}</span>
              );
            })}
          </div>
          <textarea
            ref={textareaRef}
            className="border rounded px-2 py-1.5 text-sm w-full min-h-[80px] bg-white resize-none text-transparent caret-slate-800"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === '/') {
                e.preventDefault();
                setOpen(true);
                setQuery('');
                requestAnimationFrame(updateDropdownPosition);
                return;
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setQuery('');
                return;
              }
              if (e.key === 'Backspace') {
                const handled = removeTemplateAtCursor('backspace');
                if (handled) {
                  e.preventDefault();
                  return;
                }
              }
              if (e.key === 'Delete') {
                const handled = removeTemplateAtCursor('delete');
                if (handled) {
                  e.preventDefault();
                  return;
                }
              }
              const controlKeys = new Set([
                'ArrowLeft',
                'ArrowRight',
                'ArrowUp',
                'ArrowDown',
                'Home',
                'End',
                'PageUp',
                'PageDown',
                'Tab',
              ]);
              if (open && !controlKeys.has(e.key)) {
                setOpen(false);
                setQuery('');
              }
            }}
            onScroll={(e) => {
              if (!overlayRef.current) return;
              overlayRef.current.scrollTop = e.currentTarget.scrollTop;
              overlayRef.current.scrollLeft = e.currentTarget.scrollLeft;
            }}
            placeholder="Escribe texto y usa / para insertar referencias…"
          />
        </div>
        <Button
          type="button"
          variant="neutral"
          appearance="ghost"
          size="sm"
          onClick={() => {
            setQuery('');
            setOpen((v) => {
              const next = !v;
              if (next) {
                requestAnimationFrame(updateDropdownPosition);
              }
              return next;
            });
          }}
          aria-label={`Insertar referencia en ${fieldKey}`}
        >
          /
        </Button>
      </div>

      {open && position
        ? createPortal(
            <div
              ref={dropdownRef}
              className="fixed z-[3000] max-w-[360px]"
              style={{
                top: position.top,
                left: position.left,
                width: position.width,
              }}
            >
              <div className="border border-slate-200 bg-white rounded shadow-xl p-2">
                <input
                  className="border rounded px-2 py-1 text-sm w-full"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar…"
                  autoFocus
                />
                <div className="mt-2 max-h-56 overflow-auto">
                  {filtered.length === 0 ? (
                    <div className="text-xs text-slate-500 px-2 py-2">
                      Sin resultados.
                    </div>
                  ) : (
                    filtered.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-50 flex items-center gap-2"
                        onClick={() => insertTemplate(opt.template)}
                      >
                        <span className="text-xs text-slate-800">
                          {opt.label}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default InterpolatedStringEditor;
